<template>
  <ClientOnly>
    <DmModal
      v-model="modals.search"
      :title="'Wyszukaj usługę'"
      class="garage-price-category-search-modal"
    >
      <div class="garage-price-category-search-modal__wrapper">
        <div class="garage-price-category-search-modal__input-wrapper">
          <DmInput
            v-model="form.fields.search"
            class="garage-price-category-search-modal__input"
            icon="i-search"
            label="Wyszukaj usługę"
            placeholder="np. wymiana oleju"
            @update:errors="updateErrors($event, 'searchServiceOrWorkspace')"
          />
        </div>
        <div class="garage-price-category-search-modal__services-wrapper">
          <GaragePriceListWithCategories
            v-if="services.length && form.fields.search.length && !typing && !loading"
            :garage-price-list-with-categories="services"
            @show-service-details="emits('show-service-details', $event)"
            :search="true"
          />
          <div v-else-if="form.fields.search.length < 3 || typing || loading">
            <div class="garage-price-category-search-modal__popular-header">Często wyszukiwane</div>
            <div
              class="garage-price-category-search-modal__popular"
              v-for="popular in popularSearch"
              @click="form.fields.search = popular"
            >
              <i class="i-search"></i> {{ popular }}
            </div>
          </div>
          <div v-else>
            <h4 class="font-weight-500"><span class="color-primary-blue">Ups... </span>nic nie znaleziono.</h4>
            <p>Nie znaleźliśmy wyszukiwanej usługi. Kliknij w przycisk poniżej i opisz problem.
            </p>
            <DmButton
              @click="setOtherTab"
              class="garage-price-list-element-modal__button"
              title="Opisz problem"
            />
          </div>
        </div>
      </div>
    </DmModal>
    <DmLoader
      v-if="loading || typing"
      icon-size="icon-lg"
    />
  </ClientOnly>
</template>

<script setup lang="ts">
import { DmModal, DmInput, DmLoader, DmButton } from '@dobrymechanik/vue-ui-v2';
import debounce from 'debounce';
import { appRequests } from '~/api/app-instance';
import type { PriceCategoryView, PriceElementView } from '~/api/docs/app';
import GaragePriceListWithCategories from './GaragePriceListWithCategories.vue';

interface IGaragePriceCategorySearchModalFormFields {
  search: string;
}

interface IProps {
  garageId: string;
  modals: {
    search: boolean;
    service: boolean;
  };
}

interface IEmits {
  (name: 'show-service-details', value: PriceElementView): void;
  (name: 'set-other-tab'): void;
  (name: 'close-search-modal'): void;
}

// const and refs
const loading = ref(false);
const typing = ref(false);
const services: Ref<PriceCategoryView[]> = ref([]);
const popularSearch = [
  'Diagnostyka komputerowa',
  'Wymiana żarówki',
  'Naprawa zawieszenia',
  'DPF',
  'Naprawa wydechu',
  'Rozrząd'
];

// emits 
const emits = defineEmits<IEmits>();

// use
const { form, valid, updateErrors, checkFormValidity } = useFormCreator<IGaragePriceCategorySearchModalFormFields>({
  search: ''
});
const route = useRoute();

// props
const props = defineProps<IProps>();

// watch
watch(() => form.value.fields.search, (value) => {
  if (value.length >= 3) {
    typing.value = true;
    debounceSearchServices();
  } else {
    services.value = [];
  }
});

// methods 
const GETgaragePriceListWithCategories = async () => {
  const data = await appRequests.getAppGaragePriceListWithCategories(props.garageId, {
    search: form.value.fields.search
  });

  return data;
};

const debounceSearchServices = debounce(searchServices, 500);

async function searchServices() {
  loading.value = true;
  typing.value = false;

  try {
    const GETgaragePriceList = GETgaragePriceListWithCategories();

    const [resServices] = await Promise.all([
      (await GETgaragePriceList)?.data,
    ]);
    services.value = resServices;

  } catch {

  } finally {
    loading.value = false;
  }
};

// methods
const setOtherTab = () => {
  emits('close-search-modal');
  emits('set-other-tab');
};

</script>

<style lang="scss">
@import url('assets/styles/components/garage-price-category-search-modal.scss');
</style>